/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { Loader2, Upload } from "lucide-react"
import { type FC, useContext, useState } from "react"
import { Button } from "../../components/ui/button"
import { RunContext } from "../../contexts/RunContext"
import { WORKFLOW_SHIPMENT_EDITS_ID } from "../../contexts/RunContext/constants"
import { useAuth } from "../../hooks/useAuth"
import { useSystemMessages } from "../../hooks/useSystemMessages"
import type { GetMyRunsQuery } from "../../services/__generated__/graphql"
import { RunStatus } from "../../services/appsync/types"
import { fireRun } from "../../utils/fireRun"

type RunRow = GetMyRunsQuery["getMyRuns"][number]

interface Props {
  selectedRun: RunRow | null
}

export const ApplyShipmentEditsButton: FC<Props> = ({ selectedRun }) => {
  const { user } = useAuth()
  const { addMessage } = useSystemMessages()
  const [busy, setBusy] = useState(false)
  const runCtx = useContext(RunContext)
  const hasRunInProgress = (runCtx?.runs ?? []).some(
    (r) =>
      r.status === RunStatus.RUN_IN_PROGRESS
      || r.status === RunStatus.RUN_TRANSFORMS_RUNNING,
  )
  const disabled = busy || hasRunInProgress || !selectedRun

  if (user?.role !== "ROOT") return null

  const handleClick = async () => {
    if (disabled) return
    // The user-facing API takes `settingsName` (the LHS of `settings_name_and_version`);
    // the orchestrator re-resolves the version server-side. Selected runs always come
    // from the run-selector dropdown which only surfaces RunCompleted runs, so the
    // settings_name_and_version field is guaranteed non-empty.
    const settingsName = selectedRun.runSettings.settingsNameAndVersion.split("|")[0]
    setBusy(true)
    try {
      await fireRun({
        inputId: selectedRun.runSettings.inputId,
        settingsName,
        workflowId: WORKFLOW_SHIPMENT_EDITS_ID,
        addMessage,
        successDescription: `Publish • input ${selectedRun.runSettings.inputId}`,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      variant="secondaryDark"
      size="sm"
      disabled={disabled}
      onClick={() => void handleClick()}
      title={
        hasRunInProgress
          ? "A run is already in progress"
          : !selectedRun
            ? "Select a run first"
            : "Trigger workflow_shipment_edits against the selected run"
      }
      css={css`
        gap: 0.375rem;
        font-size: 0.75rem;
        padding: 0.25rem 0.75rem;
        height: auto;
      `}
    >
      {busy || hasRunInProgress ? (
        <Loader2
          size={13}
          css={css`
            animation: spin 1s linear infinite;
            @keyframes spin { to { transform: rotate(360deg); } }
          `}
        />
      ) : (
        <Upload size={13} />
      )}
      <span>Publish</span>
    </Button>
  )
}
